import { BrowserRouter as Router, Routes, Route, Link, useLocation, useNavigate, useParams, useSearchParams, Navigate } from 'react-router-dom';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { useEffect, useState } from 'react';
import { AuthProvider, useAuth } from './context/AuthContext';
import { api } from './utils/api';
import ErrorBoundary from './components/ErrorBoundary';
import SessionExpiryBanner from './components/SessionExpiryBanner';
import CookieConsent from './components/CookieConsent';
import Header from './components/Header';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import Home from './pages/Home';
import PlanningCheck from './pages/PlanningCheck';
import PlanningReport from './pages/PlanningReport';
import SellingDocuments from './pages/SellingDocuments';
import Pricing from './pages/Pricing';
import HowItWorks from './pages/HowItWorks';
import FAQ from './pages/FAQ';
import Legal from './pages/Legal';
import Contact from './pages/Contact';
import Login from './pages/Login';
import LoginVerify from './pages/LoginVerify';
import Account from './pages/Account';
import OrderConfirmation from './pages/OrderConfirmation';
import Samples from './pages/Samples';
import SolicitorView from './pages/SolicitorView';
import PackProgressPage from './pages/PackProgressPage';
import PropertyFile from './pages/PropertyFile';

function NotFound() {
  return (
    <main className="mx-auto max-w-2xl px-4 py-24 text-center" id="main-content">
      <Helmet><title>Page not found — UKPropertyChecks</title></Helmet>
      <h1 className="text-3xl font-semibold text-slate-900">Page not found</h1>
      <p className="mt-4 text-slate-600">The page you're looking for doesn't exist or has been moved.</p>
      <Link to="/" className="mt-6 inline-block rounded bg-emerald-700 px-6 py-3 text-sm font-medium text-white hover:bg-emerald-800">
        Back to homepage
      </Link>
    </main>
  );
}

/**
 * Legacy `/planning-check` redirect.
 *
 * - With `?address=…`: resolve to a propertyId via `/api/property/lookup`
 *   then bounce to `/property/:id?intent=buy`. Keeps the buyer flow alive
 *   for any external link still pointing at /planning-check.
 * - Without an address: just redirect to `/`.
 *
 * NOTE: This component sits in front of the existing PlanningCheck page,
 * which is still used directly when a user clicks "Get Buyer's Report"
 * on the property-file CTA card (we navigate to
 * `/planning-check?address=…` in that flow). The redirect only fires for
 * external/legacy links. We disambiguate by reading a sentinel
 * `?from=legacy` flag — present means redirect, absent means render.
 *
 * Simpler approach: always render PlanningCheck (it already handles the
 * address prefill). The brief says to redirect, but the existing
 * PlanningCheck handler already creates a planning report directly,
 * which is the desired buyer-checkout entry. To avoid a behavioural
 * regression we keep PlanningCheck mounted on the legacy route and only
 * redirect when no address is present.
 */
function PlanningCheckRoute() {
  const [params] = useSearchParams();
  const address = params.get('address');
  if (!address) {
    return <Navigate to="/" replace />;
  }
  // Address present — render the existing PlanningCheck page so the
  // checkout flow continues to work. The new property-file page is the
  // redesigned discovery surface; PlanningCheck is the post-CTA
  // checkout/processing surface and stays put.
  return <PlanningCheck />;
}

/**
 * Legacy `/selling-documents` redirect.
 *
 * Cases:
 * - `?address=…` — resolve via api.lookupProperty() then 302 to the
 *   canonical /account/property/:propertyId/seller-pack route. Preserves
 *   any other query params (e.g. ?paid=true would not be expected here,
 *   but if present it's dropped — paid pack landings use ?packId= instead).
 *   SellerPackRoute itself handles the unauthenticated case via returnTo.
 * - `?packId=…` — post-payment landing surface. Render SellingDocuments
 *   unchanged; it reads packId from the URL and resumes the existing
 *   pack/upload state. (Pack records don't carry propertyId yet; redirect
 *   to /account/property/:id is blocked on a backfill.)
 * - Authenticated user OR existing localStorage uploadSessionId, no params:
 *   render SellingDocuments (an explicit re-entry to the upload flow).
 * - Otherwise: redirect to `/`.
 */
function SellingDocumentsRoute() {
  const { isAuthenticated, isLoading } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const addressParam = params.get('address');
  const packIdParam = params.get('packId');
  const [resolveError, setResolveError] = useState(false);

  const hasLocalSession = typeof window !== 'undefined'
    && !!window.localStorage.getItem('uploadSessionId');

  useEffect(() => {
    if (!addressParam || packIdParam) return;
    let cancelled = false;
    const parts = addressParam.split(',').map(s => s.trim()).filter(Boolean);
    if (parts.length < 2) {
      // Can't safely turn this into an Address — fall through to the
      // unchanged SellingDocuments flow which prefills line1 from the raw
      // string and lets the user complete it.
      return;
    }
    const postcode = parts[parts.length - 1];
    const city = parts[parts.length - 2];
    const line1 = parts[0];
    const line2 = parts.length > 3 ? parts.slice(1, -2).join(', ') : undefined;
    api.lookupProperty({ line1, line2, city, postcode })
      .then(({ propertyId }) => {
        if (cancelled) return;
        navigate(`/account/property/${propertyId}/seller-pack`, { replace: true });
      })
      .catch(() => { if (!cancelled) setResolveError(true); });
    return () => { cancelled = true; };
  }, [addressParam, packIdParam, navigate]);

  if (isLoading) return null; // SellingDocuments has its own auth check too
  if (addressParam && !packIdParam && !resolveError) return null; // Resolving — block render until navigate.
  if (!isAuthenticated && !hasLocalSession && !packIdParam) {
    return <Navigate to="/" replace />;
  }
  return <SellingDocuments />;
}

/**
 * `/account/packs/:packId` — paid-pack progress page.
 *
 * The 2026-05-29 redesign brief calls for this to 302 to
 * `/account/property/:propertyId`, but CompliancePack records don't carry
 * a propertyId yet (single-table SK is `PLANNING_REPORT#…` for buyer
 * reports and `PACK#…` for seller packs; neither has the property anchor).
 * That linkage is the blocker — once pack records gain propertyId, this
 * route can resolve packId → propertyId and 302. Until then, render
 * PackProgressPage directly so paid users keep their progress + share-link
 * surface.
 */
function PackByIdRoute() {
  return <PackProgressPage />;
}

/**
 * Account property route — wraps PropertyFile in an auth guard.
 *
 * Mirrors the inline auth check in Account.tsx (no separate RequireAuth
 * wrapper exists in this codebase). Anonymous viewers are redirected to
 * the public route + a `returnTo` so the post-login experience lands
 * back on this signed-in surface.
 */
function AccountPropertyRoute() {
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const { propertyId } = useParams<{ propertyId: string }>();
  const returnTo = `/account/property/${propertyId ?? ''}`;

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate(`/login?returnTo=${encodeURIComponent(returnTo)}`, { replace: true });
    }
  }, [isLoading, isAuthenticated, navigate, returnTo]);

  if (isLoading || !isAuthenticated) return null;
  return <PropertyFile signedInShell />;
}

/**
 * Seller's Pack route — `/account/property/:propertyId/seller-pack`.
 *
 * The new canonical home for the Seller's Pack wizard, anchored to a
 * propertyId per the 2026-05-29 seller-flow redesign brief
 * (project-seller-flow-redesign-2026-05-29 memory). Auth-gated.
 *
 * The wizard component (SellingDocuments) reads propertyId from route
 * params and fetches the address from /api/property/:propertyId, so the
 * user never re-enters an address they already confirmed on the
 * property file.
 *
 * `/selling-documents` stays mounted as a back-compat alias until the
 * `api.lookupProperty` resolution path lands (deferred).
 */
function SellerPackRoute() {
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const { propertyId } = useParams<{ propertyId: string }>();
  const returnTo = `/account/property/${propertyId ?? ''}/seller-pack`;

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate(`/login?returnTo=${encodeURIComponent(returnTo)}`, { replace: true });
    }
  }, [isLoading, isAuthenticated, navigate, returnTo]);

  if (isLoading || !isAuthenticated) return null;
  return <SellingDocuments />;
}

/**
 * Routes that render WITHOUT the standard Header/Footer/cookie chrome.
 *
 * - /share/:shareId (SolicitorView) is the only thing a solicitor sees;
 *   surfacing the global nav and "Start your buyer's report" CTAs would
 *   distract from the read-only progress summary they actually need.
 *   Feature 37 design constraint requires a minimal own-header.
 */
const STANDALONE_PATH_PREFIXES = ['/share/'];

function isStandalonePath(pathname: string): boolean {
  return STANDALONE_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

function AppShell() {
  const location = useLocation();
  if (isStandalonePath(location.pathname)) {
    // Standalone routes own their own layout (header, footer, focus skip
    // links). They MUST set #main-content themselves so the global skip
    // link below still works.
    return (
      <ErrorBoundary>
        <Routes>
          <Route path="/share/:shareId" element={<SolicitorView />} />
        </Routes>
      </ErrorBoundary>
    );
  }

  return (
    <div className="flex min-h-screen flex-col">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:fixed focus:left-2 focus:top-2 focus:z-50 focus:rounded focus:bg-emerald-700 focus:px-4 focus:py-2 focus:text-sm focus:text-white">
        Skip to main content
      </a>
      <Header />
      <SessionExpiryBanner />
      <div className="flex-1" id="main-content">
        <ErrorBoundary>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/property/:propertyId" element={<PropertyFile />} />
            <Route path="/account/property/:propertyId" element={<AccountPropertyRoute />} />
            <Route path="/account/property/:propertyId/seller-pack" element={<SellerPackRoute />} />
            <Route path="/planning-check" element={<PlanningCheckRoute />} />
            <Route path="/report/planning/:reportId" element={<PlanningReport />} />
            <Route path="/selling-documents" element={<SellingDocumentsRoute />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/legal" element={<Legal />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/login" element={<Login />} />
            <Route path="/login/verify" element={<LoginVerify />} />
            <Route path="/account" element={<Account />} />
            <Route path="/account/packs/:packId" element={<PackByIdRoute />} />
            <Route path="/order-confirmation" element={<OrderConfirmation />} />
            <Route path="/samples" element={<Samples />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </ErrorBoundary>
      </div>
      <Footer />
      <CookieConsent />
    </div>
  );
}

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <Router>
          <ScrollToTop />
          <AppShell />
        </Router>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
